import React, { useState } from "react";

import type { FormProps } from "antd";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  notification,
  Radio,
  Row,
  Select,
} from "antd";
import { useDepositMoneyMutation } from "store/api";
import { useSelector } from "react-redux";
import { selectUser } from "store/slices";
import { useNavigate } from "react-router-dom";

type FieldType = {
  accountNumber?: string;
  password?: string;
  accountType?: string;
  documentName?: string;
  documentDescription?: string;
  amount?: string;
  transactionDate?: string;
  transactionId?: string;
  transactionType?: string;
  paymentMethod?: string;
};

const DepositForm: React.FC = () => {
  const [depositMoney, { isLoading }] = useDepositMoneyMutation();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const type = params.get("type");
  const [maxLength, setMaxLength] = useState(11);

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      await depositMoney({ ...user, ...values }).unwrap();
      notification.success({
        message: "Deposit Success",
        description: "You have successfully deposited money",
      });
      navigate("/dashboard/overview");
    } catch (error) {
      notification.error({
        message: "Deposit Failed",
        description: "Please try again",
      });
    }
  };

  return (
    <div>
      <Form name="basic" onFinish={onFinish} autoComplete="off">
        <Row gutter={[14, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Select Transaction Type"
              layout="vertical"
              name="transactionType"
              rules={[
                { required: true, message: "Please select transaction type" },
              ]}
              className="mb-12 sm:mb-14 "
            >
              <Select
                defaultValue={type}
                allowClear
                options={[
                  { value: "", label: "Select Transaction Type" },
                  { value: "savings", label: "Savings" },
                  { value: "membership", label: "Membership" },
                  { value: "subscription", label: "Subscription" },
                ]}
                placeholder="select it"
                className="bg-slate-100 !w-full h-10"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Select Account Number"
              layout="vertical"
              name="accountNumber"
              rules={[
                { required: true, message: "Account number is required" },
              ]}
              className="mb-12 sm:mb-14 "
            >
              <Select
                defaultValue=""
                allowClear
                options={[
                  { value: "", label: "Select Account Number" },
                  { value: user?.accountNumber, label: user?.accountNumber },
                ]}
                placeholder="select it"
                className="bg-slate-100 w-full h-10"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Amount"
              layout="vertical"
              name="amount"
              rules={[{ required: true, message: "Amount is required" }]}
              className="mb-12 sm:mb-14 "
            >
              <Input className="bg-slate-100 h-10" type="number" min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[14, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Transaction Date"
              layout="vertical"
              name="transactionDate"
              rules={[{ required: true, message: "Please provide the date" }]}
              className="mb-12 sm:mb-14 "
            >
              <DatePicker
                className="w-full h-10"
                placeholder="YYYY-MM-DD"
                allowClear={false}
                placement="bottomRight"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Payment Method"
              layout="vertical"
              name="paymentMethod"
              rules={[
                { required: true, message: "Please select a payment method" },
              ]}
              initialValue="mtn"
              className="mb-12 sm:mb-14"
            >
              <Radio.Group
                options={[
                  { label: "MTN Mobile Money", value: "mtn" },
                  { label: "Airtel Money", value: "airtel" },
                  { label: "Bank", value: "bank" },
                ]}
                optionType="button"
                buttonStyle="solid"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Transaction ID"
              layout="vertical"
              name="transactionId"
              dependencies={["paymentMethod"]}
              rules={[
                { required: true, message: "Please input transaction Id" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const method = getFieldValue("paymentMethod");

                    if (!value) return Promise.resolve();

                    if (!/^\d+$/.test(value)) {
                      return Promise.reject(
                        new Error("Transaction ID must contain only numbers"),
                      );
                    }

                    if (method === "mtn" && value.length !== 11) {
                      setMaxLength(11);
                      return Promise.reject(
                        new Error("MTN Transaction ID must be 11 digits"),
                      );
                    }
                    if (
                      (method === "airtel" || method === "bank") &&
                      value.length !== 12
                    ) {
                      setMaxLength(12);
                      return Promise.reject(
                        new Error(
                          `${method === "airtel" ? "Airtel" : "Bank"} Transaction ID must be 12 digits`,
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              className="mb-12 sm:mb-14"
            >
              <Input
                className="bg-slate-100 h-10"
                inputMode="numeric"
                maxLength={maxLength}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className="mb-3 text-center mt-4">
          <Button
            type="primary"
            htmlType="submit"
            className="w-auto !min-w-40 font-semibold"
            loading={isLoading}
          >
            Deposit
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default DepositForm;
